import React, { Fragment, useState, useRef, useMemo, useEffect } from "react"
import InputContainer from "../helpers/InputContainer"
import { Badge } from 'reactstrap'
import { CustomInput } from '@src/components/reactstrap-compat'
import { components } from 'react-select'

const Menu = (props) => {
  const extraRemoteParams = _.get(props.selectProps, 'extraRemoteParams', [])
  return (
    <Fragment>
      <components.Menu {...props}>
        {props.children}
        {props.selectProps.extraRemoteParams && (
          <div className="d-flex flex-column px-75 py-50 bg-light">
            {_.map(extraRemoteParams, (item, index) => {
              const { key, title, type = 'checkbox', id, name, param, onParamChange } = item
              return (
                <div key={index} className="d-inline-flex my-50" onClick={(e) => onParamChange(e, key, param)}>
                  <CustomInput
                    id={id}
                    name={name}
                    type={type}
                    label={(<span className="font-weight-normal">{title}</span>)}
                    checked={param}
                    readOnly
                  />
                </div>
              )
            })}
          </div>
        )}
      </components.Menu>
    </Fragment>
  )
}

const Option = (props) => {
  const { children, data } = props
  return (
    <components.Option {...props}>
      {!props.selectProps.optionFormatter && children}
      {props.selectProps.optionFormatter && props.selectProps.optionFormatter({ children, data })}
    </components.Option>
  )
}

const ValueContainer = (props) => {
  return (
    <components.ValueContainer {...props}>
      {React.Children.map(props.children, children => {
        return React.Children.map(children, (child) => {
          return React.Children.map(child, (c, i) => {
            if (c.type.name === 'MultiValue') {
              return (
                <div className="multi-value-container">{React.cloneElement(c, { ...c.props })}</div>
              )
            }
            return React.cloneElement(c, { ...c.props })
          })
        })
      })}
    </components.ValueContainer>
  )
}

const AsyncSelectWithActions = (props) => {
  const cache = useRef({})
  const [data, setData] = useState({ isLoading: false, search: '', extraRemoteParams: {}, options: [] })
  const loadOptions = async (q, params) => {
    // Check if the result is already cached
    if (props.cacheOptions) {
      if (cache.current[q]) {
        return cache.current[q]
      }
    }
    // Fetch options from the API
    const options = await props.loadOptions(q, params)
    // Cache the result
    if (props.cacheOptions) {
      cache.current[q] = options
    }
    // Return options from the API
    return options
  }

  const debouncedLoadOptions = useMemo(
    () => _.debounce(async (newValue) => {
      setData((data) => ({ ...data, isLoading: true }))
      const options = await loadOptions(newValue, data.extraRemoteParams)
      setData((data) => ({ ...data, isLoading: false, options }))
    }, 300),
    [data.extraRemoteParams]
  )

  const onInputChange = async (newValue) => {
    setData((data) => ({ ...data, search: newValue }))
    debouncedLoadOptions.cancel()
    debouncedLoadOptions(newValue)
  }

  const handleParamChange = async (e, key, param) => {
    e.preventDefault()
    if (props.cacheOptions) {
      cache.current = {}
    }
    const params = {
      ...data.extraRemoteParams,
      [key]: !param
    }
    setData((data) => ({ ...data, isLoading: true, extraRemoteParams: params }))
    const options = await loadOptions(data.search, params)
    setData((data) => ({ ...data, isLoading: false, options }))
  }

  const loadDefaultOptions = async () => {
    if (props.defaultOptions) {
      const options = await loadOptions('')
      setData((data) => ({ ...data, options: _.size(options) > 0 ? options : [] }))
    }
  }

  useEffect(() => {
    loadDefaultOptions()
  }, [])

  return (
    <InputContainer
      {...props}
      type={'uncontrolled-select'}
      // menuIsOpen
      inputValue={data.search}
      onInputChange={onInputChange}
      options={data.options}
      isLoading={data.isLoading}
      extraRemoteParams={props.extraRemoteParams ? _.map(props.extraRemoteParams, ({ key, title, type }) => ({
        key,
        title,
        type,
        id: `async-select-${key || 'param'}-key`,
        name: `async-select-${key || 'param'}-key`,
        param: _.get(data.extraRemoteParams, key, false),
        onParamChange: handleParamChange
      })) : false}
      optionFormatter={props.optionFormatter}
      components={{
        ...props.components,
        Menu,
        Option,
        ValueContainer
      }}
    />
  )
}

export default AsyncSelectWithActions